import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, catchError, map, of, shareReplay, take } from 'rxjs';
import { Router, RouterModule } from '@angular/router';

import { Table } from '../../shared/table/table';
import { TableAction, TableColumn } from '../../shared/models/table-models';
import { Popup } from '../../../core/shared/components/popup/popup';
import { faEye, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { ToastService } from '../../../core/shared/services/toast.service';
import { DropdownOption, PKL_GRP } from '../../../core/shared/models/picklist.model';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { Setup } from '../shared/service/setup';
import { GuarantorPayload } from '../shared/models/guarantor.model';

interface Guarantor {
  srNo: number;
  stpGrntrSeq: number | null;
  name: string;
  totalAmount: number | null;
  coverageAmountPmic: number | null;
  coverageAmountClient: number | null;
  tenure: number | null;
  stsKey: number | null;
  crncySeq: number | null;
  /** Shown in the table; the list API only sends `crncySeq`. */
  currencyName: string;
}

@Component({
  selector: 'app-guarantor-setup',
  imports: [TranslatePipe, Table, Popup, RouterModule],
  templateUrl: './guarantor-setup.html',
  styleUrl: './guarantor-setup.css',
})
export class GuarantorSetup implements OnInit {
  showPopup = false;
  faPencil = faPencil;
  faEye = faEye;
  faTrash = faTrash;
  popupTitle = '';
  isDeleteConfirm = false;
  isViewMode = false;
  deleteMessage = '';

  constructor(
    private translate: TranslateService,
    private router: Router,
    private setupService: Setup,
    private picklist: PickListService,
    private toast: ToastService
  ) { }

  /** Active currencies (`/setup/grntr/get-act-crncy-list`) for the Currency dropdown. */
  currencyOptions$: Observable<DropdownOption[]> = of([]);

  /** `crncySeq` -> name from `/setup/grntr/get-crncy-list` (all currencies, so inactive ones still have a name). */
  private currencyNames: Record<number, string> = {};

  ngOnInit(): void {
    this.picklist.loadCodes([PKL_GRP.COMMON_STATUS]);

    this.currencyOptions$ = this.setupService.getActiveCurrencyList().pipe(
      map((res: any) => {
        const list: any[] = Array.isArray(res) ? res : res?.data || [];

        return list
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => ({ label: String(item.crncyNm ?? ''), value: Number(item.crncySeq) }));
      }),
      catchError((err) => {
        console.error('Failed to load currencies', err);
        return of([]);
      }),
      shareReplay(1)
    );

    this.setupService.getCurrencyList().subscribe({
      next: (res: any) => {
        const list: any[] = Array.isArray(res) ? res : res?.data || [];

        this.currencyNames = {};
        list.forEach((item) => {
          this.currencyNames[Number(item.crncySeq)] = String(item.crncyNm ?? '');
        });

        // Rows may have loaded first — name their currency now.
        this.guarantors = this.guarantors.map((row) => ({ ...row, currencyName: this.currencyName(row.crncySeq) }));
      },
      error: (err) => console.error('Failed to load currency names', err)
    });

    this.picklist.picklists$.pipe(take(1)).subscribe(() => {
      this.buildColumns();
    });

    this.loadGuarantors();
  }

  popupFields: DynamicFieldConfig[] = [];

  // ================= TABLE CONFIG =================
  columns: TableColumn[] = [];

  buildColumns(): void {
    this.columns = [
      { key: 'srNo', label: 'SETUP.GUARANTOR_SETUP.SR_NO', type: 'text' },
      {
        key: 'name',
        label: 'SETUP.GUARANTOR_SETUP.NAME',
        type: 'link',
        onClick: (row) => this.router.navigate(['/guarantor-client-list'], { queryParams: { stpGrntrSeq: row.stpGrntrSeq } })
      },
      { key: 'totalAmount', label: 'SETUP.GUARANTOR_SETUP.TOTAL_AMOUNT', type: 'currency' },
      { key: 'currencyName', label: 'SETUP.GUARANTOR_SETUP.CURRENCY', type: 'text' },
      { key: 'coverageAmountPmic', label: 'SETUP.GUARANTOR_SETUP.COVERAGE_AMOUNT_PMIC', type: 'currency' },
      { key: 'coverageAmountClient', label: 'SETUP.GUARANTOR_SETUP.COVERAGE_AMOUNT_CLIENT', type: 'currency' },
      { key: 'tenure', label: 'SETUP.GUARANTOR_SETUP.TENURE', type: 'currency' },
      {
        key: 'stsKey',
        label: 'COMMON.STATUS',
        type: 'badge',
        map: this.getStatusMap(),
        badgeMap: this.getStatusBadgeMap()
      }
    ];
  }

  getStatusMap(): Record<number, string> {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    const map: Record<number, string> = {};

    options.forEach((option) => {
      map[option.value] = option.label;
    });

    return map;
  }

  getStatusBadgeMap(): Record<number, string> {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    // Rows always carry 706 (active) / 707 (inactive), so the pill colour must not depend on the picklist label text.
    const map: Record<number, string> = { 706: 'status-active', 707: 'status-inactive' };

    options.forEach((option) => {
      const label = option.label?.trim().toLowerCase();

      if (label === 'active') {
        map[option.value] = 'status-active';
      } else if (label === 'inactive') {
        map[option.value] = 'status-inactive';
      } else if (!map[option.value]) {
        map[option.value] = 'status-default';
      }
    });

    return map;
  }

  getDefaultStatus(): number {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    return options?.[0]?.value || 706;
  }

  guarantors: Guarantor[] = [];
  loading = false;

  loadGuarantors() {
    this.loading = true;
    this.setupService.getGuarantorList().subscribe({
      next: (res: any) => {
        this.loading = false;
        const list: any[] = Array.isArray(res) ? res : res?.data || [];

        this.guarantors = list
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item, index) => ({
            srNo: index + 1,
            stpGrntrSeq: this.toNumber(item.stpGrntrSeq),
            name: item.grntrNm ?? '',
            totalAmount: this.toNumber(item.ttlAmnt),
            coverageAmountPmic: this.toNumber(item.cvrgPmic),
            coverageAmountClient: this.toNumber(item.cvrgClnt),
            tenure: this.toNumber(item.tnr),
            stsKey: this.toNumber(item.stsKey),
            crncySeq: this.toNumber(item.crncySeq),
            currencyName: this.currencyName(this.toNumber(item.crncySeq))
          }));
      },
      error: (err) => {
        this.loading = false;
        console.error('Failed to load guarantors', err);
      }
    });
  }

  selectedRow: Guarantor | null = null;

  private buildFields(row: Partial<Guarantor> | null, required: boolean): DynamicFieldConfig[] {
    return [
      {
        label: 'SETUP.GUARANTOR_SETUP.NAME',
        key: 'name',
        type: 'text',
        value: row?.name ?? '',
        validations: required ? { required: true, textOnly: true } : undefined
      },
      {
        label: 'SETUP.GUARANTOR_SETUP.TOTAL_AMOUNT',
        key: 'totalAmount',
        type: 'text',
        value: row?.totalAmount ?? '',
        validations: required ? { required: true, numbersOnly: true } : undefined
      },
      {
        label: 'SETUP.GUARANTOR_SETUP.COVERAGE_AMOUNT_PMIC',
        key: 'coverageAmountPmic',
        type: 'text',
        value: row?.coverageAmountPmic ?? '',
        maxlength: 2,
        validations: required ? { required: true, numbersOnly: true, maxLength: 2 } : undefined
      },
      {
        label: 'SETUP.GUARANTOR_SETUP.COVERAGE_AMOUNT_CLIENT',
        key: 'coverageAmountClient',
        type: 'text',
        value: row?.coverageAmountClient ?? '',
        maxlength: 2,
        validations: required ? { required: true, numbersOnly: true, maxLength: 2 } : undefined
      },
      {
        label: 'SETUP.GUARANTOR_SETUP.TENURE',
        key: 'tenure',
        type: 'text',
        value: row?.tenure ?? '',
        maxlength: 2,
        validations: required ? { required: true, numbersOnly: true, maxLength: 2 } : undefined
      },
      {
        label: 'COMMON.STATUS',
        key: 'stsKey',
        type: 'select',
        value: row?.stsKey ?? this.getDefaultStatus(),
        options$: this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS),
        validations: required ? { required: true } : undefined
      },
      {
        label: 'SETUP.GUARANTOR_SETUP.CURRENCY',
        key: 'crncySeq',
        type: 'select',
        value: row?.crncySeq ?? null,
        options$: this.currencyOptions$,
        validations: required ? { required: true } : undefined
      }
    ];
  }

  openGuarantor() {
    this.selectedRow = null;
    this.popupTitle = 'SETUP.GUARANTOR_SETUP.ADD_GUARANTOR';
    this.isDeleteConfirm = false;
    this.isViewMode = false;
    this.popupFields = this.buildFields(null, true);
    this.showPopup = true;
  }

  // ================= TABLE ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.faPencil,
      class: 'text-indigo-600',
      tooltip: 'COMMON.EDIT',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.GUARANTOR_SETUP.EDIT_GUARANTOR';
        this.isDeleteConfirm = false;
        this.isViewMode = false;
        this.popupFields = this.buildFields(row, true);
        this.showPopup = true;
      }
    },
    {
      icon: this.faEye,
      class: 'text-green-600',
      tooltip: 'COMMON.VIEW',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.GUARANTOR_SETUP.VIEW_GUARANTOR';
        this.isDeleteConfirm = false;
        this.isViewMode = true;
        this.popupFields = this.buildFields(row, false);
        this.showPopup = true;
      }
    },
    {
      icon: this.faTrash,
      class: 'text-red-600',
      tooltip: 'COMMON.DELETE',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.GUARANTOR_SETUP.DELETE_GUARANTOR';
        this.isDeleteConfirm = true;
        this.isViewMode = false;
        this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.name });
        this.showPopup = true;
      }
    }
  ];

  /**
   * Returns false so the popup stays open: on a validation error the user
   * fixes the values in place, and on save the popup is closed only once the
   * API succeeds (`showPopup = false`), so an API error keeps the entered data.
   */
  SaveGuarantor(formData: any): boolean {
    const coverageTotal =
      Number(formData.coverageAmountPmic ?? 0) + Number(formData.coverageAmountClient ?? 0);

    if (coverageTotal > 100) {
      this.toast.error(this.translate.instant('SETUP.GUARANTOR_SETUP.COVERAGE_SUM_INVALID', { total: coverageTotal }));
      return false;
    }

    const payload = this.createPayload(formData);

    this.setupService.saveGuarantor(payload).subscribe({
      next: (res: any) => {
        // A 200 can still carry an error body: the error interceptor has already toasted it.
        if (res?.__hasError) {
          return;
        }

        if (res?.error) {
          this.toast.error(res.error);
          return;
        }

        this.toast.success('SETUP.SAVED_SUCCESSFULLY');
        this.showPopup = false;
        this.loadGuarantors();
      },
      error: (err) => {
        console.error('Save failed', err);
        this.toast.error('SETUP.FAILED_TO_SAVE_RECORD');
      }
    });

    return false;
  }

  onDeleteConfirmed(): void {
    const payload = this.createPayload({});

    this.setupService.saveGuarantor(payload).subscribe({
      next: (res: any) => {
        if (res?.__hasError) {
          return;
        }

        if (res?.error) {
          this.toast.error(res.error);
          return;
        }

        this.toast.success('SETUP.DELETED_SUCCESSFULLY');
        this.showPopup = false;
        this.loadGuarantors();
      },
      error: (err) => {
        console.error('Delete failed', err);
        this.toast.error('SETUP.FAILED_TO_DELETE_RECORD');
      }
    });
  }

  private createPayload(formData: any): GuarantorPayload {
    const isDelete = this.isDeleteConfirm;
    const row = this.selectedRow;

    // If Delete, use existing values. If Edit/Create, use Form values.
    return {
      stpGrntrSeq: row?.stpGrntrSeq ?? null,
      grntrNm: (isDelete && row) ? row.name : formData.name,
      ttlAmnt: Number((isDelete && row) ? row.totalAmount : formData.totalAmount),
      cvrgPmic: Number((isDelete && row) ? row.coverageAmountPmic : formData.coverageAmountPmic),
      cvrgClnt: Number((isDelete && row) ? row.coverageAmountClient : formData.coverageAmountClient),
      tnr: Number((isDelete && row) ? row.tenure : formData.tenure),
      stsKey: Number((isDelete && row) ? row.stsKey : formData.stsKey),
      delFlg: isDelete ? 1 : 0,
      crncySeq: this.toNumber((isDelete && row) ? row.crncySeq : formData.crncySeq)
    };
  }

  private currencyName(crncySeq: number | null): string {
    if (crncySeq == null) {
      return '-';
    }

    return this.currencyNames[crncySeq] ?? String(crncySeq);
  }

  private toNumber(value: unknown): number | null {
    return value === null || value === undefined || value === '' ? null : Number(value);
  }
}
